import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, Facebook, Youtube, Linkedin, Send } from "lucide-react";

const Footer = () => {
    const currentYear = new Date().getFullYear();

    return (
        <footer className="bg-neutral-900 border-t border-neutral-800 pt-16 pb-6 w-full text-gray-300 relative overflow-hidden">
            {/* Subtle Blueprint Grid Pattern */}
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMSIgY3k9IjEiIHI9IjEiIGZpbGw9InJnYmEoMjU1LCAyNTUsIDI1NSwgMC4wMikiLz48L3N2Zz4=')] pointer-events-none"></div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* Main Footer Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 mb-16">
                    {/* Column 1: Brand & About */}
                    <div className="flex flex-col">
                        <Link
                            to="/"
                            className="flex items-center gap-4 mb-6 group w-fit"
                        >
                            <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center p-1 border border-neutral-700 group-hover:border-yellow-400 transition-colors">
                                <img
                                    src="/assets/logo.png"
                                    alt="PYQ Pedia Logo"
                                    className="w-full h-full object-contain"
                                />
                            </div>
                            <div className="flex flex-col">
                                <h2 className="text-2xl font-black text-white uppercase tracking-wider">
                                    PYQ Pedia
                                </h2>
                                <p className="text-xs uppercase text-yellow-400 font-bold">
                                    Master the past, Ace the future.
                                </p>
                            </div>
                        </Link>
                        <p className="text-sm leading-relaxed text-gray-400 font-medium pr-4">
                            India’s trusted online coaching platform for UPSC,
                            PSC, SSC, and other competitive exams. Empowering
                            aspirants with expert mentorship, strict daily
                            targets, and high-quality study materials.
                        </p>
                    </div>

                    {/* Column 2: Quick Links */}
                    <div className="lg:pl-8">
                        <h3 className="text-white font-black uppercase text-sm mb-6 flex items-center">
                            Quick Links
                        </h3>
                        <ul className="flex flex-col space-y-4">
                            {[
                                { name: "Home", path: "/" },
                                { name: "About Us", path: "/about" },
                                {
                                    name: "Recent Vacancies",
                                    path: "/recent-vacancies/all",
                                },
                                {
                                    name: "Current Affairs",
                                    path: "/current-affairs",
                                },
                                { name: "Blogs & Insights", path: "/blogs" },
                            ].map((link, idx) => (
                                <li key={idx}>
                                    <Link
                                        to={link.path}
                                        className="text-sm text-gray-400 hover:text-white transition-colors flex items-center group w-fit font-medium"
                                    >
                                        <span className="w-2 h-0.5 bg-red-600 mr-3 group-hover:w-5 group-hover:bg-yellow-400 transition-all duration-300"></span>
                                        {link.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Column 3: Student Support */}
                    <div>
                        <h3 className="text-white font-black uppercase text-sm mb-6 flex items-center">
                            Support
                        </h3>
                        <ul className="flex flex-col space-y-4">
                            {[
                                { name: "Contact Us", path: "/contact" },
                                {
                                    name: "Privacy Policy",
                                    path: "/privacy-policy",
                                },
                                {
                                    name: "Terms & Conditions",
                                    path: "/terms-condition",
                                },
                                {
                                    name: "Refund Policy",
                                    path: "/refund-policy",
                                },
                            ].map((link, idx) => (
                                <li key={idx}>
                                    <Link
                                        to={link.path}
                                        className="text-sm text-gray-400 hover:text-white transition-colors flex items-center group w-fit font-medium"
                                    >
                                        <span className="w-2 h-0.5 bg-red-600 mr-3 group-hover:w-5 group-hover:bg-yellow-400 transition-all duration-300"></span>
                                        {link.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Column 4: Contact & Social */}
                    <div>
                        <h3 className="text-white font-black uppercase text-sm mb-6 flex items-center">
                            Get in Touch
                        </h3>

                        <div className="flex flex-col space-y-5 mb-8">
                            <a
                                href="mailto:upscpyqpedia@gmail.com"
                                className="flex items-start text-sm text-gray-400 hover:text-white transition-colors group w-fit font-medium"
                            >
                                <Mail
                                    size={16}
                                    className="text-yellow-400 mr-3 mt-0.5"
                                />
                                upscpyqpedia@gmail.com
                            </a>
                            <a
                                href="tel:+918709364253"
                                className="flex items-start text-sm text-gray-400 hover:text-white transition-colors group w-fit font-medium"
                            >
                                <Phone
                                    size={16}
                                    className="text-yellow-400 mr-3 mt-0.5"
                                />
                                +91 8709364253
                            </a>
                        </div>
                    </div>
                </div>

                {/* Bottom Copyright Bar */}
                <div className="pt-6 border-t border-neutral-800 flex flex-col md:flex-row justify-between items-center gap-4">
                    <p className="text-xs text-gray-500 font-medium">
                        © {currentYear}{" "}
                        <span className="text-gray-300 font-bold">
                            PYQ Pedia
                        </span>
                        . All rights reserved.
                    </p>
                    <p className="text-xs text-gray-500 font-medium">
                        Designed & Developed by{" "}
                        <a
                            href="https://shinewell.in/"
                            target="_blank"
                            rel="noreferrer"
                            className="text-gray-300 hover:text-yellow-400 font-bold transition-colors"
                        >
                            Shinewell
                        </a>
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
